import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import Logo from '@/components/ui/Logo';
import './cartazes.css';

type Tipo = 'barraca' | 'caixa' | 'portaria';

interface Alvo {
  chave: string;
  tipo: Tipo;
  titulo: string;
  refId: number | null;
  link: string | null;
  criando: boolean;
  erro: string | null;
}

interface PontoBruto {
  ponto_id: number | string;
  nome: string;
  ativo?: boolean;
}
interface CaixaBruto {
  caixa: number | string;
  nome: string;
  ativo?: boolean;
}
interface ConviteBruto {
  convite_id: number | string;
  nome: string;
  funcoes: string;
  situacao: string;
  link: string;
}
interface QuemBruto {
  convite_id: number | string;
  e_caixa?: boolean;
  caixa_id?: number | string | null;
  link?: string;
}

const partes = (f: string): string[] =>
  (f || '')
    .split('·')
    .map((s) => s.trim())
    .filter(Boolean);

const conviteAtivo = (s: string): boolean => s !== 'cancelado' && s !== 'vencido';

export default function CartazesImprimir() {
  const { eventoId } = useParams<{ eventoId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, pronto } = useAuth();
  const idNum = Number(eventoId);

  const tipoParam = searchParams.get('tipo');
  const tipo: Tipo | 'todos' =
    tipoParam === 'barraca' || tipoParam === 'caixa' || tipoParam === 'portaria'
      ? tipoParam
      : 'todos';
  // Cuidado: Number(null) é 0 (não NaN). Sem parâmetro, o filtro tem que ser
  // "sem filtro" (NaN). Sem isso, a busca da tela cheia filtra por id 0 e some com tudo.
  const numeroDoParam = (nome: string): number => {
    const bruto = searchParams.get(nome);
    if (bruto == null || bruto.trim() === '') return NaN;
    const n = Number(bruto);
    return Number.isFinite(n) ? n : NaN;
  };
  const pontoFiltro = numeroDoParam('ponto');
  const caixaFiltro = numeroDoParam('caixa');

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [nomeEvento, setNomeEvento] = useState('');
  const [alvos, setAlvos] = useState<Alvo[]>([]);

  useEffect(() => {
    if (!pronto) return;
    if (!user) {
      navigate(`/entrar?voltar=${encodeURIComponent(`/gerenciar/${idNum}/cartazes`)}`, {
        replace: true,
      });
      return;
    }
    let ativo = true;
    (async () => {
      setCarregando(true);
      setErro(null);
      try {
        const [rPt, rCx, rCo, rQ, rPo, ev] = await Promise.all([
          supabase.rpc('loja_pontos_do_evento', { p_evento: idNum }),
          supabase.rpc('caixa_caixas_do_evento', { p_evento: idNum }),
          supabase.rpc('equipe_convites_do_evento', { p_evento: idNum }),
          supabase.rpc('equipe_quem_e_quem', { p_evento: idNum }),
          supabase.rpc('equipe_portaria_do_evento', { p_evento: idNum }),
          supabase.from('eventos').select('nome').eq('id', idNum).maybeSingle(),
        ]);
        if (rPt.error) throw rPt.error;
        if (rCx.error) throw rCx.error;
        if (rCo.error) throw rCo.error;

        const eventoNome = ((ev.data as { nome?: string } | null)?.nome ?? '').trim();

        const pontos = (((rPt.data as PontoBruto[]) || []) as PontoBruto[]).filter(
          (p) => p.ativo !== false,
        );
        const caixas = (((rCx.data as CaixaBruto[]) || []) as CaixaBruto[]).filter(
          (c) => c.ativo !== false,
        );
        const convites = ((rCo.data as ConviteBruto[]) || []) as ConviteBruto[];
        const quem = ((rQ.data as QuemBruto[]) || []) as QuemBruto[];
        const portariaStaff = ((rPo.data as unknown[]) || []).length;

        const ativos = convites.filter((c) => conviteAtivo(c.situacao));

        // Convite de caixa: casamos pelo caixa escolhido (equipe_quem_e_quem traz o caixa_id).
        const linkPorCaixa = new Map<number, string>();
        quem.forEach((q) => {
          const cid = Number(q.convite_id);
          const c = convites.find((x) => Number(x.convite_id) === cid);
          if (q.e_caixa && q.caixa_id != null && c && conviteAtivo(c.situacao)) {
            linkPorCaixa.set(Number(q.caixa_id), q.link || c.link);
          }
        });

        const acharBarraca = (nome: string) =>
          ativos.find((c) => partes(c.funcoes).includes(nome.trim()));
        const acharPortaria = () => ativos.find((c) => partes(c.funcoes).includes('Portaria'));

        const incluirBarraca = tipo === 'todos' || tipo === 'barraca';
        const incluirCaixa = tipo === 'todos' || tipo === 'caixa';
        const temConvitePortaria = ativos.some((c) => partes(c.funcoes).includes('Portaria'));
        const incluirPortaria =
          tipo === 'portaria' ||
          (tipo === 'todos' && (portariaStaff > 0 || temConvitePortaria));

        const lista: Alvo[] = [];

        if (incluirBarraca) {
          pontos
            .filter((p) => !Number.isFinite(pontoFiltro) || Number(p.ponto_id) === pontoFiltro)
            .forEach((p) => {
              const c = acharBarraca(p.nome);
              lista.push({
                chave: `ponto-${p.ponto_id}`,
                tipo: 'barraca',
                titulo: p.nome,
                refId: Number(p.ponto_id),
                link: c?.link ?? null,
                criando: !c,
                erro: null,
              });
            });
        }

        if (incluirCaixa) {
          caixas
            .filter((c) => !Number.isFinite(caixaFiltro) || Number(c.caixa) === caixaFiltro)
            .forEach((c) => {
              const link = linkPorCaixa.get(Number(c.caixa)) ?? null;
              lista.push({
                chave: `caixa-${c.caixa}`,
                tipo: 'caixa',
                titulo: c.nome,
                refId: Number(c.caixa),
                link,
                criando: !link,
                erro: null,
              });
            });
        }

        if (incluirPortaria) {
          const c = acharPortaria();
          lista.push({
            chave: 'portaria',
            tipo: 'portaria',
            titulo: 'Portaria',
            refId: null,
            link: c?.link ?? null,
            criando: !c,
            erro: null,
          });
        }

        if (!ativo) return;
        setNomeEvento(eventoNome);
        setAlvos(lista);
        setCarregando(false);

        // Cria (uma vez) os convites que faltam, para cada função.
        for (const a of lista.filter((x) => x.criando)) {
          if (!ativo) return;
          try {
            const { data, error } = await supabase.rpc('equipe_criar_convite', {
              p_evento: idNum,
              p_nome: a.tipo === 'portaria' ? 'Cartaz · Portaria' : `Cartaz · ${a.titulo}`,
              p_telefone: null,
              p_caixa: a.tipo === 'caixa',
              p_pontos: a.tipo === 'barraca' && a.refId != null ? [a.refId] : [],
              p_portaria: a.tipo === 'portaria',
            });
            if (error) throw error;
            const resp = data as { ok?: boolean; erro?: string; link?: string; convite_id?: number };
            if (!resp || resp.ok === false) {
              throw new Error(resp?.erro || 'Não foi possível criar o link.');
            }
            // Caixa: fixa o caixa do cartaz no convite (igual à tela de convite).
            if (a.tipo === 'caixa' && resp.convite_id && a.refId != null) {
              await supabase.rpc('equipe_definir_caixa', {
                p_convite: resp.convite_id,
                p_caixa: a.refId,
              });
            }
            if (!ativo) return;
            setAlvos((cur) =>
              cur.map((x) =>
                x.chave === a.chave ? { ...x, link: resp.link ?? null, criando: false } : x,
              ),
            );
          } catch (e) {
            if (!ativo) return;
            setAlvos((cur) =>
              cur.map((x) =>
                x.chave === a.chave
                  ? { ...x, criando: false, erro: mensagemAmigavel(e, 'Não consegui gerar o link.') }
                  : x,
              ),
            );
          }
        }
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e, 'Não foi possível carregar as barracas.'));
          setCarregando(false);
        }
      }
    })();
    return () => {
      ativo = false;
    };
  }, [idNum, navigate, tipo, pontoFiltro, caixaFiltro, pronto, user]);

  if (carregando) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100">
        <span className="flex h-12 w-12 items-center justify-center text-primary-500">
          <i className="ri-loader-4-line animate-spin text-4xl" />
        </span>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] rounded-2xl border border-secondary-200 bg-white p-8 text-center">
          <h1 className="text-xl font-medium text-foreground-950">Não foi possível abrir</h1>
          <p className="mt-2 text-sm text-foreground-600">{erro}</p>
          <button
            type="button"
            onClick={() => navigate(`/gerenciar/${idNum}?aba=equipe`)}
            className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-xl bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            Voltar ao painel
          </button>
        </div>
      </div>
    );
  }

  const preparando = alvos.some((a) => a.criando);

  return (
    <div className="pagina-cartaz min-h-screen bg-background-100">
      {/* Barra superior — fora da impressão */}
      <div className="no-print border-b border-secondary-200 bg-white">
        <div className="mx-auto flex w-full max-w-[880px] flex-wrap items-center justify-between gap-3 px-4 py-4">
          <button
            type="button"
            onClick={() => navigate(`/gerenciar/${idNum}?aba=equipe`)}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm font-medium text-secondary-600 transition-colors hover:text-foreground-900"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-left-line" />
            </span>
            Voltar
          </button>
          <div className="flex items-center gap-3">
            {preparando && (
              <span className="flex items-center gap-1.5 text-[13px] text-secondary-500">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-loader-4-line animate-spin" />
                </span>
                Preparando os cartazes…
              </span>
            )}
            <button
              type="button"
              onClick={() => window.print()}
              disabled={alvos.length === 0 || preparando}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-xl bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-printer-line" />
              </span>
              Imprimir cartazes
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[880px] px-4 py-8">
        {alvos.length === 0 ? (
          <div className="rounded-2xl border border-secondary-200 bg-white p-8 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
              <i className="ri-store-2-line text-2xl" />
            </span>
            <p className="mx-auto mt-4 max-w-sm text-[15px] text-foreground-800">
              Não há nenhuma barraca, caixa ou portaria cadastrada para gerar cartaz.
            </p>
          </div>
        ) : (
          alvos.map((a) => (
            <article key={a.chave} className="folha-cartaz">
              <header className="flex items-center justify-between border-b border-gray-300 pb-3">
                <Logo imgClassName="h-10 w-auto object-contain" />
                <p className="text-right text-[13px] font-semibold">{nomeEvento}</p>
              </header>

              <div className="flex flex-1 flex-col items-center justify-center py-8 text-center">
                {a.tipo === 'portaria' ? (
                  <p className="text-[14px] uppercase tracking-[0.3em] text-gray-500">Portaria</p>
                ) : (
                  <p className="text-[14px] uppercase tracking-[0.3em] text-gray-500">
                    {a.tipo === 'caixa' ? 'Caixa' : 'Servo desta barraca'}
                  </p>
                )}

                <h1 className="mt-2 text-[40px] font-extrabold leading-tight">{a.titulo}</h1>

                <div className="mt-8">
                  {a.link ? (
                    <QRCodeSVG value={a.link} size={340} level="M" />
                  ) : a.criando ? (
                    <div className="flex h-[340px] w-[340px] items-center justify-center rounded border border-dashed border-gray-300 text-[13px] text-gray-500">
                      Preparando o link…
                    </div>
                  ) : (
                    <div className="flex h-[340px] w-[340px] items-center justify-center rounded border border-dashed border-gray-300 px-6 text-center text-[13px] text-red-600">
                      {a.erro || 'Não foi possível gerar o link.'}
                    </div>
                  )}
                </div>

                <p className="mt-8 max-w-[150mm] text-[18px] leading-relaxed">
                  <strong>Servo desta barraca:</strong> aponte a câmera do celular, abra o link e
                  toque em <strong>“Abrir a função neste celular”</strong>.
                </p>

                {a.link && (
                  <p className="mt-3 break-all text-[13px] text-gray-600">{a.link}</p>
                )}
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
}